import type { ProRenderFieldPropsType } from '@ant-design/pro-provider'
import type { PostalAddress } from '@app/icare/db/schema/common'
import { getGoogleMapsUrl } from '@pkg/ui/links/getGoogleMapsUrl'
import { Space } from 'antd'
import type { ReactNode } from 'react'
import { FaLocationArrow, FaMapLocationDot } from 'react-icons/fa6'

const renderPostalAddress = (address: PostalAddress, multiline = false): ReactNode => {
  const line1 = address.streetAddress || '-'
  const line2 = [address.zipCode, address.city].filter(Boolean).join(' ') || '-'

  if (!multiline) {
    return [line1, line2].filter(Boolean).join(', ') || '-'
  }

  return (
    <Space vertical size="small">
      <span>{line1}</span>
      <span>{line2}</span>
    </Space>
  )
}

export interface MapsLinkValue {
  lat?: number
  lng?: number
  address?: PostalAddress | string | null
  multiline?: boolean
}

/**
 * Custom ProComponents value type for rendering map links.
 * Supports both lat/lng coordinates and address strings/objects.
 */
export const mapsLinkValueType: ProRenderFieldPropsType = {
  render: (text, _p) => {
    if (!text) return '-'

    let url: string | null = null
    let displayText: ReactNode = '-'

    // Handle lat/lng coordinates
    if (typeof text === 'object' && 'lat' in text && 'lng' in text) {
      const { lat, lng } = text as MapsLinkValue
      if (lat != null && lng != null) {
        url = `https://www.google.com/maps?q=${lat},${lng}`
        displayText = `${lat}, ${lng}`
      }
    }
    // Handle PostalAddress object
    else if (typeof text === 'object' && 'streetAddress' in text) {
      const address = text as PostalAddress
      url = getGoogleMapsUrl(address)
      displayText = renderPostalAddress(address)
    }
    // Handle address string
    else if (typeof text === 'string') {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(text)}`
      displayText = text
    }
    // Handle MapsLinkValue with address
    else if (typeof text === 'object' && 'address' in text) {
      const { address, multiline } = text as MapsLinkValue
      if (typeof address === 'string') {
        url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`
        displayText = address
      } else if (address && typeof address === 'object') {
        url = getGoogleMapsUrl(address)
        displayText = renderPostalAddress(address, Boolean(multiline))
      }
    }

    if (!url) return displayText

    return (
      <Space style={{ alignItems: 'start' }}>
        <FaMapLocationDot className="inline mr-1.5 text-xs text-gray-400" />
        <span>{displayText}</span>
        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
          <FaLocationArrow className="inline ml-1.5 text-xs text-gray-400" />
        </a>
      </Space>
    )
  },
  renderFormItem: (text, props) => props?.fieldProps?.value || text,
}
